import { Button } from 'components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from 'components/ui/form';
import { Input } from 'components/ui/input';
import { Textarea } from 'components/ui/textarea';
import { useDebounce } from 'hooks';
import { CheckCircle, FileWarning } from 'lucide-react';
import { TEST_IDS } from 'mocks/ids';
import * as yup from 'yup';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { validationSchema } from 'utils/validation';
import { yupResolver } from '@hookform/resolvers/yup';
import { ICreateReferenceMutation, IReferences } from 'modules/threads/types';
import { Spinner } from 'ui';
import { useCheckGoogleLink } from '../utils';

const validation = yup.object({
  name: validationSchema.stringRequired(),
  description: validationSchema.textArea(),
  link: validationSchema.url().required(),
});

type Props = {
  reference?: IReferences;
  onSubmit: (payload: Omit<ICreateReferenceMutation, 'thread'>) => Promise<void>;
  isSubmitting: boolean;
};

export const LinkForm = ({ reference, onSubmit, isSubmitting }: Props) => {
  const form = useForm({
    resolver: yupResolver(validation),
    defaultValues: reference
      ? { name: reference.name, link: reference.url, description: reference.description ?? '' }
      : { name: '', link: '', description: '' },
    mode: 'onChange',
  });

  const link = form.watch('link');
  const debouncedLink = useDebounce(link, 600);

  const { status, fileName } = useCheckGoogleLink(
    debouncedLink,
    !form.getFieldState('link').invalid
  );

  // Set reference name to google doc title
  useEffect(() => {
    if (!fileName) return;
    form.setValue('name', fileName);
  }, [fileName]);

  const handleSubmit = async (fields: yup.InferType<typeof validation>) => {
    const payload = {
      referenceType: 'LINK' as const,
      name: fields.name,
      description: fields.description ?? null,
      url: fields.link,
      file: null,
    };

    await onSubmit(payload);
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="space-y-4 px-1"
        data-testid={TEST_IDS.add_reference_link_tab}
      >
        <FormField
          control={form.control}
          name="link"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Link <span className="text-app-red">*</span>
                {status === 'checking' && (
                  <span className="pl-2 text-xs italic text-app-gray">Checking link...</span>
                )}
              </FormLabel>
              <FormControl>
                <Input aria-label="link-input" type="url" placeholder="Link" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {status === 'inaccessible' && (
          <div className="flex flex-row items-center gap-2 text-xs text-primary">
            <div className="h-4 w-4">
              <FileWarning className="h-4 w-4" />
            </div>
            Your google account does not have access to this resource. Comments sync and
            spreadsheets links highlighting will not work.
          </div>
        )}

        {status === 'error' && (
          <div className="flex flex-row items-center gap-2 text-xs text-app-red">
            <div className="h-4 w-4">
              <FileWarning className="h-4 w-4" />
            </div>
            Failed to check this link for Google integration
          </div>
        )}

        {status === 'accessible' && (
          <div className="flex flex-row items-center gap-2 text-xs text-blue">
            <div className="h-4 w-4">
              <CheckCircle className="h-4 w-4" />
            </div>
            Google Integration available for this link
          </div>
        )}
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Name <span className="text-app-red">*</span>
              </FormLabel>
              <FormControl>
                <Input aria-label="name-input" placeholder="Name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea aria-label="description-input" placeholder="Description" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex w-full flex-row justify-end">
          <Button
            disabled={isSubmitting || status === 'loading' || status === 'checking'}
            type="submit"
          >
            Save
            {isSubmitting && (
              <div className="pl-2">
                <Spinner color="white" size="small" />
              </div>
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};
